import type {
  TBookAlias,
  TBookCover,
  TBookWrapping,
  TProductAlias,
  TProductOption,
} from '@shared/jp/product';
import type { IPrice, IBookCover } from '@shared/jp/data/products';
import type { IShipping } from '@shared/jp/data/shipping';
import {
  calculateDiscount,
  getDiscountCommon,
  getShippingDiscountCommon,
} from '@shared/common/discount/utils';
import type { TCouponData, TWithDocId } from '@shared/common/interfaces';
import type { IBookInOrder, IPaymentInformation } from '@shared/jp/interfaces';
import { Logger } from '@shared/common/logger';
import type { TBookData } from '@shared/jp/models';
import type { PartialUndefined } from '@studiobuki/web-core';
import type { ICalculatorComponentItem } from '@studiobuki/web-core/lib/calculator';
import type StripeBE from 'stripe';
import {
  BOOK_ALIAS_FROEBELKAN,
  BOOK_ALIAS_FROEBELKAN_ALPHABET_TRAIN,
  isBookFroebelkanData,
  COVER_IMAGE_FROEBELKAN_VALUE_TO_LABEL_MAP,
  RIBBON_COLOR_FROEBELKAN_VALUE_TO_LABEL_MAP,
  KUNCHAN_VALUE_TO_LABEL_MAP,
  CANDLE_CHARACTER_FROEBELKAN_VALUE_TO_LABEL_MAP,
  isBookFroebelkanAlphabetTrainData,
  isBookMainichiKinenbiData,
  BOOK_ALIAS_FROEBELKAN_MAINICHI_KINENBI,
} from '@shared/jp/product';
import { parseBirthTime } from '@shared/common/product';
import format from 'date-fns/format';
import type { TShippingMethod } from '@shared/jp/shipping/interfaces';
import { getHeroName } from '@shared/jp/utils';
import memoizee from 'memoizee';
import type { IBookCoverCommon } from '@shared/common/data/products';
import jpJP from '@shared/common/translations/ja-JP.json';
import {
  getProductConfig,
  getProductPrice as _getProductPrice,
} from '@shared/common/products';
import type { TProduct } from '@shared/common/products';
import type {
  ICartData,
  IDiscountCampaignCommon,
} from '@shared/common/discount/interfaces';
import { EVendor, type TVendor } from '@shared/jp/enums';
import { firstValueFrom } from 'rxjs';
import { CURRENCY } from '@shared/jp/constants';
import { localSharedProductsArr } from '@shared/jp/vendors/froebelkan/data/products';
import { getKidstitle } from './app/pages/book-page/pages/book-froebelkan-page/segments-utils';
import type { IProduct, ISpecs } from './types';
import { getBookByAlias } from './app/data/books/utils';
import { wrappingDefault, wrappings } from './static';
import type { DiscountService } from './app/services/discount.service';

type TJPKey = keyof typeof jpJP;

export const scrollToElement = (
  element: Element,
  container = document.documentElement,
  _yGap: number | null = null,
): void => {
  const { x, y } = element.getBoundingClientRect();
  let yGap = _yGap || 0;

  if (_yGap === null && container === document.documentElement) {
    const styles = getComputedStyle(container);
    const topbarHeight = parseInt(
      styles.getPropertyValue('--buki-topbar-height'),
      10,
    );
    const headerHeight = parseInt(
      styles.getPropertyValue('--buki-header-height'),
      10,
    );

    yGap = topbarHeight + headerHeight;
  }

  container.scrollBy({
    top: y - yGap,
    left: x,
    behavior: 'smooth',
  });
};

const scrollToSelectorLog = new Logger('scrollToSelector');
export const scrollToSelector = (
  selector: string,
  container = document.documentElement,
  _yGap = null,
): void => {
  const log = scrollToSelectorLog;
  const element = document.querySelector(selector);

  if (!element) {
    log.error('element not found by selector', selector);
    return;
  }

  scrollToElement(element, container, _yGap);
};

export const checkStripeCoupon = (coupon: any): coupon is StripeBE.Coupon =>
  coupon && ('percent_off' in coupon || 'amount_off' in coupon);

export const getCouponDiscount = ({
  percent_off,
  amount_off,
}: StripeBE.Coupon): string => {
  if (percent_off) {
    return `${percent_off}%`;
  }
  if (amount_off) {
    return `${amount_off}`;
  }

  return '0';
};

export const getCalculatorItems = async (
  getDiscount$: DiscountService['getDiscount$'],
  getShippingDiscount$: DiscountService['getShippingDiscount$'],
  products: IProduct[],
  couponData?: TCouponData,
  shipping?: IShipping,
) => {
  const calculatorItems: ICalculatorComponentItem[] = [];

  let booksPrice = 0;
  let booksPriceAfterDiscount = 0;

  const booksRes = await Promise.all(
    products.map(async (p) => {
      const price = p.cover.price.value + p.wrapping.price.value;

      booksPrice += price;

      let priceAfterDiscount = price;

      const discount = await firstValueFrom(getDiscount$(p.alias));

      if (discount) {
        priceAfterDiscount = calculateDiscount(priceAfterDiscount, discount);
      }

      if (checkStripeCoupon(couponData)) {
        priceAfterDiscount = calculateDiscount(
          priceAfterDiscount,
          getCouponDiscount(couponData),
        );
      }

      booksPriceAfterDiscount += priceAfterDiscount;

      return {
        name: '商品',
        price,
        currency: CURRENCY,
      };
    }),
  );

  calculatorItems.push(...booksRes);

  if (booksPrice !== booksPriceAfterDiscount) {
    calculatorItems.push({
      name: '割引',
      price: booksPriceAfterDiscount - booksPrice,
      currency: CURRENCY,
      discount: true,
    });
  }

  if (shipping) {
    const shippingMethod = shipping.id as TShippingMethod;
    let shippingPrice = shipping.price;

    const shippingDiscount = await firstValueFrom(
      getShippingDiscount$(shippingMethod),
    );

    if (shippingDiscount) {
      shippingPrice = calculateDiscount(shippingPrice, shippingDiscount);
    }

    calculatorItems.push({
      name: '送料',
      price: shippingPrice,
      currency: shipping.currency,
    });
  }

  return calculatorItems;
};

export const createBookInOrderObject = (
  alias: TBookAlias,
  bookId: string,
  wrapping: TBookWrapping,
  cover: TBookCover,
): IBookInOrder => ({
  alias,
  bookId,
  status: 0,
  wrapping,
  cover,
});

export const FEToBEProduct = ({
  alias,
  bookId,
  wrapping,
  cover,
}: IProduct): IBookInOrder =>
  createBookInOrderObject(alias, bookId, wrapping.id, cover.id);

export const getProductPrice = (p: IProduct): IPrice => ({
  value: p.cover.price.value + p.wrapping.price.value,
  currency: p.cover.price.currency,
});

export const getCovers = memoizee(<T extends TProductAlias>(alias: T) => {
  const book = getBookByAlias(alias as TBookAlias);

  return book.options;
});

export const getCover = (alias: TProductAlias, cover: TProductOption) => {
  const covers = getCovers(alias);

  return covers[cover as keyof typeof covers] as IBookCoverCommon;
};
const getBookDataSpecsLog = new Logger('getBookDataSpecs');

export const getBookDataSpecs = (
  bookData: TBookData,
  exceptFields: string[] = [],
): ISpecs => {
  const log = getBookDataSpecsLog;

  const messageText = 'messageText' in bookData ? bookData.messageText : '';

  const heroName = getHeroName(bookData);

  const specs: ISpecs = {
    /** hero's name */
    名前: heroName,
  };

  if (isBookFroebelkanData(bookData)) {
    const {
      birthDate,
      birthTime,
      kunChan,
      ageToBe,
      coverImage,
      coverRibbon,
      birthHeight,
      height,
      birthWeight,
      weight,
      messageText2,
      characters,
    } = bookData;
    let hour: number | undefined;
    let minute: number | undefined;
    if (birthTime) {
      const _ = parseBirthTime(birthTime);
      hour = _.hour;
      minute = _.minute;
    }
    Object.assign(specs, {
      'くん・ちゃん付け': `${KUNCHAN_VALUE_TO_LABEL_MAP[kunChan]}`,

      /** birthDate */
      生年月日: format(birthDate, 'yyyy/MM/dd'),
      /** Age to be */
      何歳の誕生日: `${ageToBe}歳`,
      /** Cover color */
      表紙の色: `${COVER_IMAGE_FROEBELKAN_VALUE_TO_LABEL_MAP[coverImage]}`,
      /** Ribbon color */
      'リボン (イラスト) の色': `${RIBBON_COLOR_FROEBELKAN_VALUE_TO_LABEL_MAP[coverRibbon]}`,
      /** BirthHours - BirthMinutes */
      出生時間: birthTime && `${hour}時${minute}分`,
      うまれた時の身長: `${birthHeight}センチ`,
      今の身長: `${height}センチ`,
      うまれた時の体重: `${birthWeight}グラム`,
      今の体重: `${weight}キログラム`,
      /** photo */
      写真: 'あり',
      /** CandleCharacter */
      ロウソクのキャラクター: `${Object.entries(characters)
        .filter(([_, value]) => value)
        .map(([key]) => CANDLE_CHARACTER_FROEBELKAN_VALUE_TO_LABEL_MAP[key])
        .join('・')}`,
      /** dedication */
      'メッセージ（左ページ）': messageText,
      'メッセージ（右ページ）': messageText2,
    });

    if (!birthTime) {
      delete specs['出生時間'];
    }
  } else if (isBookFroebelkanAlphabetTrainData(bookData)) {
    const { kunChan, skinColor, hairColor, hairStyle, glasses, vehicle } =
      bookData;
    Object.assign(specs, {
      'くん・ちゃん付け': `${KUNCHAN_VALUE_TO_LABEL_MAP[kunChan]}`,
      肌色: jpJP[`alphabet-train.skin.${skinColor}` as TJPKey],
      髪色: jpJP[`alphabet-train.hair.${hairColor}` as TJPKey],
      髪型: jpJP[`alphabet-train.hair-style.${hairStyle}` as TJPKey],
      メガネ: glasses ? 'あり' : 'なし',
      乗り物: jpJP[`alphabet-train.vehicle.${vehicle}` as TJPKey],
      写真: '写真あり',
      メッセージ: messageText,
    });
  } else if (isBookMainichiKinenbiData(bookData)) {
    const { kunChan, heroName, coverColor } = bookData;
    Object.assign(specs, {
      名前: `${heroName}`,
      'くん・ちゃん付け': `${KUNCHAN_VALUE_TO_LABEL_MAP[kunChan]}`,
      表紙の色:
        jpJP[`mainichi-kinenbi.cover-color.${bookData.coverColor}` as TJPKey],
      '●記念日1': ` `,
      '・記念日名(1)': `${bookData.anniversaryNames[0]} `,
      '・日付(1)': `${bookData.anniversaryYears[0]}ねん ${bookData.anniversaryMonths[0]}がつ ${bookData.anniversaryDays[0]}にち`,
      '・テキスト(1)': `${bookData.anniversaryMessages[0]} `,
      '・デザイン(1)':
        jpJP[
          `mainichi-kinenbi.design.${bookData.backgroundDesigns[0]}` as TJPKey
        ],
      '・背景色(1)':
        jpJP[
          `mainichi-kinenbi.color.${bookData.backgroundColors[0]}` as TJPKey
        ],
      '・写真枠(1)':
        jpJP[
          `mainichi-kinenbi.photodesign.${bookData.photoframeDesigns[0]}` as TJPKey
        ],
      '●記念日2': ` `,
      '・記念日名(2)': `${bookData.anniversaryNames[1]} `,
      '・日付(2)': `${bookData.anniversaryYears[1]}ねん ${bookData.anniversaryMonths[1]}がつ ${bookData.anniversaryDays[1]}にち`,
      '・テキスト(2)': `${bookData.anniversaryMessages[1]} `,
      '・デザイン(2)':
        jpJP[
          `mainichi-kinenbi.design.${bookData.backgroundDesigns[1]}` as TJPKey
        ],
      '・背景色(2)':
        jpJP[
          `mainichi-kinenbi.color.${bookData.backgroundColors[1]}` as TJPKey
        ],
      '・写真枠(2)':
        jpJP[
          `mainichi-kinenbi.photodesign.${bookData.photoframeDesigns[1]}` as TJPKey
        ],
      '●記念日3': ` `,
      '・記念日名(3)': `${bookData.anniversaryNames[2]} `,
      '・日付(3)': `${bookData.anniversaryYears[2]}ねん ${bookData.anniversaryMonths[2]}がつ ${bookData.anniversaryDays[2]}にち`,
      '・テキスト(3)': `${bookData.anniversaryMessages[2]} `,
      '・デザイン(3)':
        jpJP[
          `mainichi-kinenbi.design.${bookData.backgroundDesigns[2]}` as TJPKey
        ],
      '・背景色(3)':
        jpJP[
          `mainichi-kinenbi.color.${bookData.backgroundColors[2]}` as TJPKey
        ],
      '・写真枠(3)':
        jpJP[
          `mainichi-kinenbi.photodesign.${bookData.photoframeDesigns[2]}` as TJPKey
        ],
      '●記念日4': ` `,
      '・記念日名(4)': `${bookData.anniversaryNames[3]} `,
      '・日付(4)': `${bookData.anniversaryYears[3]}ねん ${bookData.anniversaryMonths[3]}がつ ${bookData.anniversaryDays[3]}にち`,
      '・テキスト(4)': `${bookData.anniversaryMessages[3]} `,
      '・デザイン(4)':
        jpJP[
          `mainichi-kinenbi.design.${bookData.backgroundDesigns[3]}` as TJPKey
        ],
      '・背景色(4)':
        jpJP[
          `mainichi-kinenbi.color.${bookData.backgroundColors[3]}` as TJPKey
        ],
      '・写真枠(4)':
        jpJP[
          `mainichi-kinenbi.photodesign.${bookData.photoframeDesigns[3]}` as TJPKey
        ],
      '●記念日5': ` `,
      '・記念日名(5)': `${bookData.anniversaryNames[4]} `,
      '・日付(5)': `${bookData.anniversaryYears[4]}ねん ${bookData.anniversaryMonths[4]}がつ ${bookData.anniversaryDays[4]}にち`,
      '・テキスト(5)': `${bookData.anniversaryMessages[4]} `,
      '・デザイン(5)':
        jpJP[
          `mainichi-kinenbi.design.${bookData.backgroundDesigns[4]}` as TJPKey
        ],
      '・背景色(5)':
        jpJP[
          `mainichi-kinenbi.color.${bookData.backgroundColors[4]}` as TJPKey
        ],
      '・写真枠(5)':
        jpJP[
          `mainichi-kinenbi.photodesign.${bookData.photoframeDesigns[4]}` as TJPKey
        ],
      '●記念日6': ` `,
      '・記念日名(6)': `${bookData.anniversaryNames[5]} `,
      '・日付(6)': `${bookData.anniversaryYears[5]}ねん ${bookData.anniversaryMonths[5]}がつ ${bookData.anniversaryDays[5]}にち`,
      '・テキスト(6)': `${bookData.anniversaryMessages[5]} `,
      '・デザイン(6)':
        jpJP[
          `mainichi-kinenbi.design.${bookData.backgroundDesigns[5]}` as TJPKey
        ],
      '・背景色(6)':
        jpJP[
          `mainichi-kinenbi.color.${bookData.backgroundColors[5]}` as TJPKey
        ],
      '・写真枠(6)':
        jpJP[
          `mainichi-kinenbi.photodesign.${bookData.photoframeDesigns[5]}` as TJPKey
        ],
      '●記念日7': ` `,
      '・記念日名(7)': `${bookData.anniversaryNames[6]} `,
      '・日付(7)': `${bookData.anniversaryYears[6]}ねん ${bookData.anniversaryMonths[6]}がつ ${bookData.anniversaryDays[6]}にち`,
      '・テキスト(7)': `${bookData.anniversaryMessages[6]} `,
      '・デザイン(7)':
        jpJP[
          `mainichi-kinenbi.design.${bookData.backgroundDesigns[6]}` as TJPKey
        ],
      '・背景色(7)':
        jpJP[
          `mainichi-kinenbi.color.${bookData.backgroundColors[6]}` as TJPKey
        ],
      '・写真枠(7)':
        jpJP[
          `mainichi-kinenbi.photodesign.${bookData.photoframeDesigns[6]}` as TJPKey
        ],
      奥付メッセージ: messageText,
    });
  } else {
    log.error(`didn't recognize`, { bookData });
  }

  // Object.assign(specs, {
  //   /** cover */
  //   表紙の種類: cover ? `「${jpJP[cover]}」` : '',
  // });

  // removing excepted fields
  exceptFields.forEach((field) => delete specs[field]);

  // removing empty fields
  Object.keys(specs).forEach((key) => specs[key] || delete specs[key]);

  return specs;
};

const getProductNameLog = new Logger('getProductName');
export const getProductName = (
  alias: TBookAlias,
  heroName: string,

  // gender?: TGender | TGenderGrandpaGrandma,
  // childrenNames?: string[],
) => {
  const log = getProductNameLog;

  switch (alias) {
    case BOOK_ALIAS_FROEBELKAN:
      return `${heroName} おたんじょうびおめでとう`;
    case BOOK_ALIAS_FROEBELKAN_ALPHABET_TRAIN:
      return `${heroName} おなまえさがして あいうえおもちゃごう`;
    case BOOK_ALIAS_FROEBELKAN_MAINICHI_KINENBI:
      return `${heroName} の まいにちきねんび`;
    default:
      log.error(`alias "${alias}" not handled yet`);
      return '';
  }
};

type TGetProductRequiredFields = 'alias' | 'bookId' | 'hero' | 'specs';

type TGetProductExcludedFields = 'covers' | 'wrappings' | 'name';

type TGetProductFields = Required<Pick<IProduct, TGetProductRequiredFields>> &
  PartialUndefined<
    Omit<IProduct, TGetProductRequiredFields | TGetProductExcludedFields>
  >;

export const getProduct = ({
  alias,
  bookId,
  cover,
  hero,
  children,
  gender,
  image,
  wrapping,
  specs,
}: TGetProductFields): IProduct => {
  const book = getBookByAlias(alias);

  const p: IProduct = {
    alias,
    bookId,
    name: getProductName(alias, hero),
    covers: Object.values(book.options),
    cover: cover || book.options[book.option],
    hero,
    image: image || book.menuPhoto,
    wrappings,
    wrapping: wrapping || wrappingDefault,
    specs,
  };

  if (children) {
    p.children = children;
  }

  if (gender) {
    p.gender = gender;
  }

  return p;
};

export const getProductByBookData = (bookData: TBookData) =>
  getProduct({
    alias: bookData.alias as TBookAlias,
    bookId: bookData.bookId,
    hero:
      getHeroName(bookData) +
      ('kunChan' in bookData ? getKidstitle(bookData.kunChan) : ''),
    gender: 'gender' in bookData ? bookData.gender : undefined,
    children:
      'grandchildren' in bookData
        ? bookData.grandchildren.data.map(({ childrenName }) => childrenName)
        : undefined,
    wrapping: ((wrapping: TBookWrapping | undefined) =>
      wrapping && wrappings.find((w) => w.id === wrapping))(bookData.wrapping),
    cover: getCover(bookData.alias, bookData.cover) as IBookCover,
    specs: getBookDataSpecs(bookData),
  });

const getPaymentInformationLog = new Logger('getPaymentInformation');
export const getPaymentInformation = (
  type: IPaymentInformation['type'],
  cartData: ICartData<TProductAlias, TProductOption, TBookWrapping>,
  // cartData: {
  //   books: IBookSimpleData[];
  //   products?: IProductInOrder[] | undefined;
  // },
  /** discount is not calculated for `number` */
  shippingData:
    | {
        shippingPrice: number;
        shippingMethod: TShippingMethod;
      }
    | number,
  configData:
    | {
        productsConfig?: Record<string, TWithDocId<TProduct>> | undefined;
        discountCampaign?: IDiscountCampaignCommon<TVendor> | undefined;
      }
    | undefined,
  couponData?: TCouponData | undefined,
  additionalPrice = 0,
) => {
  const log = getPaymentInformationLog;
  const discountCampaign = configData?.discountCampaign;
  const productsConfig = configData?.productsConfig;
  const productsConfigArr = productsConfig ? Object.values(productsConfig) : [];
  const { books, products } = cartData;

  /** raw books price */
  const productSubtotal: IPaymentInformation['productSubtotal'] = books.reduce(
    (totalPrice, { alias, cover, count: _count }) => {
      const count = _count || 1;
      let bookPrice = getCover(alias, cover).price.value * count;

      const discount =
        discountCampaign &&
        getDiscountCommon(
          discountCampaign,
          alias,
          productsConfigArr,
          localSharedProductsArr,
          EVendor.froebelkan,
          cartData,
        );

      if (discount) bookPrice = calculateDiscount(bookPrice, discount);

      return totalPrice + bookPrice + additionalPrice * count;
    },
    products.reduce((totalPrice, userProduct) => {
      const alias = userProduct.productId;
      const productConfig = productsConfig?.[alias];

      if (!productConfig) {
        log.error('product config not found by productId', {
          productsConfig,
          userProduct,
        });
        return totalPrice;
      }

      const config = getProductConfig(
        productConfig.config,
        userProduct.options,
      );

      let productPrice = _getProductPrice(
        config,
        productConfig.steps,
        userProduct.activeSectionsMap,
      );

      // apply discount campaign
      const discount =
        discountCampaign &&
        getDiscountCommon(
          discountCampaign,
          alias,
          productsConfigArr,
          localSharedProductsArr,
          EVendor.froebelkan,
          cartData,
        );

      if (discount) productPrice = calculateDiscount(productPrice, discount);

      return totalPrice + productPrice + additionalPrice;
    }, 0) || 0,
  );

  /** final books price */
  let productTotal = productSubtotal;
  let couponCode: IPaymentInformation['couponCode'];

  // apply stripe coupon
  if (checkStripeCoupon(couponData)) {
    productTotal = calculateDiscount(
      productTotal,
      getCouponDiscount(couponData),
    );
    couponCode = couponData.id;
  }

  // define shipping price
  let shippingPrice = 0;

  if (typeof shippingData === 'number') {
    shippingPrice = shippingData;
  } else {
    const shippingDiscount =
      discountCampaign &&
      getShippingDiscountCommon(
        discountCampaign,
        shippingData.shippingMethod,
        EVendor.froebelkan,
      );

    shippingPrice = shippingDiscount
      ? calculateDiscount(shippingData.shippingPrice, shippingDiscount)
      : shippingData.shippingPrice;
  }

  const res: IPaymentInformation = {
    discountAmount: productSubtotal - productTotal,
    productSubtotal,
    productTotal,
    shippingPrice,
    orderTotal: productTotal + shippingPrice,
    type,
    paymentMethod: 'Card',
    paymentIntent: 'undefined',
  };

  if (couponCode) {
    res.couponCode = couponCode;
  }

  return res;
};

/**
 * Checks if a specific parameter has changed between two objects.
 * @param _a The first object to compare.
 * @param _b The second object to compare.
 * @param param The parameter to check for changes.
 * @returns `true` if the parameter has changed, `false` otherwise.
 */
export const isParamChanged = <T extends { [key: string]: any }>(
  _a: T | null | undefined | false,
  _b: T | null | undefined | false,
  param: string,
) => {
  const a = _a && param in _a ? _a[param as keyof T] : null;
  const b = _b && param in _b ? _b[param as keyof T] : null;

  return a !== b;
};
